import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { detectDiagramType } from '@archer/shared';
import { Wand2, X } from 'lucide-react';

mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  securityLevel: 'loose',
  themeVariables: {
    primaryColor: '#3D9168',
    primaryTextColor: '#0f172a',
    primaryBorderColor: '#1e293b',
    lineColor: '#94a3b8',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
});

interface MermaidPanelProps {
  open: boolean;
  onClose: () => void;
  initial?: string;
  onConvert: (code: string) => Promise<void> | void;
  onAiPrompt?: () => void;
}

export const MermaidPanel = ({ open, onClose, initial = '', onConvert, onAiPrompt }: MermaidPanelProps) => {
  const [code, setCode] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const id = useRef(`mmd-${Date.now().toString(36)}`).current;

  useEffect(() => {
    if (initial) setCode(initial);
  }, [initial]);

  useEffect(() => {
    if (!open || !previewRef.current) return;
    setError(null);
    const render = async () => {
      try {
        const { svg } = await mermaid.render(id, code || 'flowchart LR\n  A --> B');
        if (previewRef.current) previewRef.current.innerHTML = svg;
      } catch (err) {
        setError((err as Error).message);
        if (previewRef.current) previewRef.current.innerHTML = '';
      }
    };
    void render();
  }, [code, open, id]);

  if (!open) return null;

  return (
    <div className="pointer-events-auto flex w-[480px] max-w-[90vw] flex-col rounded-xl border border-slate-800 bg-slate-900/95 p-4 shadow-soft backdrop-blur">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Mermaid / C4 source</h3>
        <button className="tool-btn" onClick={onClose} title="Close">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mb-1 text-xs text-slate-400">
        Type:{' '}
        <span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-archer-300">
          {code ? detectDiagramType(code) : '—'}
        </span>
      </div>
      <textarea
        className="input h-48 resize-y font-mono text-xs"
        spellCheck={false}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder={'flowchart LR\n  A[Start] --> B{Decision}\n  B -->|yes| C[(DB)]'}
      />
      <div className="mt-3 flex gap-2">
        {onAiPrompt && (
          <button className="btn-secondary flex-1" onClick={onAiPrompt}>
            <Wand2 className="h-4 w-4 text-archer-300" />
            AI generate…
          </button>
        )}
        <button
          className="btn-primary flex-1"
          onClick={() => onConvert(code)}
          disabled={!code.trim()}
        >
          Convert to canvas
        </button>
      </div>
      <div className="mt-4 border-t border-slate-800 pt-3">
        <div className="mb-2 text-xs uppercase tracking-wide text-slate-400">Preview</div>
        <div className="max-h-72 overflow-auto rounded-md border border-slate-800 bg-slate-950 p-3">
          <div ref={previewRef} className="mermaid-preview flex justify-center" />
          {error && <pre className="text-xs text-red-400">{error}</pre>}
        </div>
      </div>
    </div>
  );
};
